import algoliasearch from 'algoliasearch/lite';
import { SearchOptions } from '@algolia/client-search';
import { algolia_app_id, algolia_api_key } from '@/config';
import { SearchTransformer } from './SearchTransformer';
import { TkSearchOption } from './TkSearchOption';
import { Pagination } from '../pagination/Pagination';
import { axiosProxy } from "@/plugins/axios";
import { AxiosInstance } from 'axios';
import { Artwork } from '../artwork/Artwork';
import { DataObject } from '@/utils/DataObject';


export class SearchRepository {
  private api: AxiosInstance = axiosProxy;

  client = algoliasearch(algolia_app_id, algolia_api_key);

  // Prepare payload for new algolia v4
  private prepareSearchPayload(searchOption: TkSearchOption): SearchOptions {
    return {
      query: searchOption.query,
      page: searchOption.page,
      filters: searchOption.filters,
      numericFilters: searchOption.numericFilters,
      hitsPerPage: searchOption.hitsPerPage || 20
    };
  }

  async search(searchOption: TkSearchOption) {
    console.log({ searchOption });
    const index = this.client.initIndex(searchOption.index);
    try {
      const res = await index.search(searchOption.query, this.prepareSearchPayload(searchOption));

      return {
        data: res.hits.map((artwork) => new SearchTransformer().toClass(artwork, searchOption.locale)),
        pagination: new Pagination({
          currentPage: res.page + 1, // Note algolia retutns zero-based page number 
          lastPage: res.nbPages,
          total: res.nbHits,
          perPage: res.hitsPerPage
        })
      }
    } catch (error) {
      throw error
    }
  }

  
  async colorSearch(searchOption: TkSearchOption) {
    console.log(searchOption);

    try {
      const payload = {
        query: searchOption.query,
        page: searchOption.page,
        filters: searchOption.filters,
        // numericFilters: searchOption.numericFilters
      };
      if (searchOption.numericFilters && searchOption.numericFilters.length) {
        payload['numericFilters'] = searchOption.numericFilters
      }


      const resp = await this.api.post(`artworks/search`, payload);
      return {
        data: resp.data.hits.map((artwork) => new SearchTransformer().toClass(artwork, searchOption.locale)),
        pagination: new Pagination({
          currentPage: resp.data.page + 1, // Note algolia retutns zero-based page number 
          lastPage: resp.data.nbPages,
          total: resp.data.nbHits,
          perPage: resp.data.hitsPerPage
        })
      }

    } catch (error) {
      throw error;
    }
  }

  async keywords(query: string): Promise<string[]> {
    const index = this.client.initIndex("keywords");
    try {
      const queryParams = { query: query, hitsPerPage: 20 }
      const res = await index.search(query, queryParams);
      const keywords: any[] = [];

      res.hits.forEach((hit: DataObject) => {
        const values = Object.values(hit.name);
        values.forEach(value => {
          if (value) keywords.push(value)
        });
      });
      return keywords;
    } catch (error) {
      throw error;
    }
  }

  async similarArtworks(artwork: Artwork, locale: any): Promise<Artwork[]> {
    try {
      const filters: string[] = [];

      if (artwork.technique) {
        filters.push(`(technique.id:${artwork.technique.id})`);
      }

       const queryParams = {
         hitsPerPage: 10,
         filters: `(technique.id:${artwork.technique ? artwork.technique.id : ''}) AND (is_for_sale=1)`,
         facetFilters: [`id:-${artwork.id}`],
         numericFilters: this.getPriceRange(artwork.price)
       };
 
      const index = this.client.initIndex("artworks_views_desc");
      const res = await index.search('', queryParams);
      return res.hits.map((hit) => new SearchTransformer().toClass(hit, locale));
      return [];
    } catch (error) {
      throw error;
    }
  }

  private getPriceRange(price?: string) {
    const priceRange = {
      min: 0,
      max: 200
    };
    if (price) {
      const _price = Number(price.replace(",", "."));

      if (_price > 200 && _price <= 500) {
        priceRange.min = 201;
        priceRange.max = 500;
      } else if (_price > 500 && _price <= 700) {
        priceRange.min = 500;
        priceRange.max = 700;
      } else if (_price > 700 && _price <= 1000) {
        priceRange.min = 700;
        priceRange.max = 1000;
      } else if (_price > 1000 && _price <= 2000) {
        priceRange.min = 1000;
        priceRange.max = 2000;
      } else if (_price > 2000 && _price <= 5000) {
        priceRange.min = 2000;
        priceRange.max = 5000;
      } else if (_price > 5000) {
        return [`price >= 5000`];
      }
    }

    return [`price >= ${priceRange.min}`, `price <= ${priceRange.max}`];
  }
}

